import { DefaultButton, DetailsList, IColumn, SelectionMode, Stack, Selection } from '@fluentui/react';
import Scope, { ScopeCtrlState } from './Scope';
import { makeAutoObservable } from 'mobx';
import { IUserConfigPackage } from '../../../API';
import { observer } from 'mobx-react';
import { MSALConfigGen } from '../../../authConfig';

export class PermissionsState {
	
	public userConfig: IUserConfigPackage;
	public scopes: ScopeCtrlState[] = [];
	isApp: boolean;

	constructor(userConfig: IUserConfigPackage, isApp:boolean) {
		this.userConfig = userConfig;
		this.isApp = isApp;


		//Set up scopes common to both app and delegated
		this.scopes = [
			new ScopeCtrlState(isApp, userConfig, false, 'Sites.Read.All', 'Required for document searching.'),
			new ScopeCtrlState(isApp, userConfig, false, 'Sites.FullControl.All', 'Required for site updating and template extraction.'),
			//new ScopeCtrlState(isApp, userConfig, false, 'Calendars.ReadWrite', 'Allows creation of task reminders in user calendars.'),
			new ScopeCtrlState(isApp, userConfig, false, 'TeamMember.ReadWrite.All', 'Allows updating of Teams members and owners.'),
			new ScopeCtrlState(isApp, userConfig, false, 'Group.Read.All', 'Allows the app to retrieve list of Teams in search dialogs.'),
			new ScopeCtrlState(isApp, userConfig, false, 'Team.Create', 'Allows creation of Teams.'),
			new ScopeCtrlState(isApp, userConfig, false, 'Group.Create', 'Allows creation of Groups, or creating Teams with sensitivity labels (following the group+teamify process).'),
			new ScopeCtrlState(isApp, userConfig, false, 'TeamSettings.ReadWrite.All', 'Allows extraction and configuration of Team settings'),
			new ScopeCtrlState(isApp, userConfig, false, 'Channel.Create', 'Allows creation of Team Channels'),
			new ScopeCtrlState(isApp, userConfig, false, 'ChannelSettings.ReadWrite.All', 'Allows extraction and configuration of Team Channels'),
			new ScopeCtrlState(isApp, userConfig, false, 'ChannelMember.ReadWrite.All', 'Allows the app to set security on private Team Channels.'),
			new ScopeCtrlState(isApp, userConfig, false, 'TeamsTab.ReadWrite.All', 'Allows extraction and configuration of Team Tabs'),
			new ScopeCtrlState(isApp, userConfig, false, 'SensitivityLabel.Read', 'Retrieve sensitivity labels available in the tenant so that they can be applied during provisioning.')
		];

		//Delegated only
		if(!isApp) {
			this.scopes = this.scopes.concat([
				new ScopeCtrlState(isApp, userConfig, true, 'AllSites.Manage', 'Required for filing of emails and documents/attachments to SharePoint.'),
				new ScopeCtrlState(isApp, userConfig, true, 'AllSites.FullControl', 'Required for site creation.'),
				new ScopeCtrlState(isApp, userConfig, false, 'People.Read', 'Allows people pickers to work in this admin portal'),
				new ScopeCtrlState(isApp, userConfig, false, 'Mail.Read', 'Allows filing of emails from the Outlook app.'),
				new ScopeCtrlState(isApp, userConfig, false, 'Mail.Send', 'Allows sending notification emails. This should be granted only for a service account.'),
				new ScopeCtrlState(isApp, userConfig, false, 'Mail.ReadWrite', 'Allows updating of messages with the correct category when other users have filed the same message.'),
				new ScopeCtrlState(isApp, userConfig, false, 'User.Read', 'View name, email and photo for the logged in user.'),
				new ScopeCtrlState(isApp, userConfig, false, 'User.ReadBasic.All', 'Allows membership lists to be built for syncing security to workspaces.'),
				new ScopeCtrlState(isApp, userConfig, false, 'Files.Read.All', 'Allows individual files to be returned in search results within the search app or outlook attachment filing app.'),
				new ScopeCtrlState(isApp, userConfig, false, 'TeamsAppInstallation.ReadWriteAndConsentForTeam', 'Allows extraction and installation of Team Apps'),
				new ScopeCtrlState(isApp, userConfig, false, 'Group.ReadWrite.All', 'Required for applying sensitivity labels under a service account model. Only required for the service account.'),
			]);
		}

		//App only
		if(isApp) {
			this.scopes = this.scopes.concat([
				new ScopeCtrlState(isApp, userConfig, true, 'Sites.FullControl.All', 'Allows creation and templating of sites, as well as filing documents and updating metadata.' ),
				new ScopeCtrlState(isApp, userConfig, false, 'TeamsAppInstallation.ReadWriteAndConsentForTeam.All', 'Allows extraction and installation of Team Apps'),
				
				//Mail.Send always applies to all mailboxes and can't be restricted via 
				//new ScopeCtrlState(isApp, userConfig, false, 'Mail.Send', 'Allows sending notification emails. This should be restricted to a specific mailbox.'),
				new ScopeCtrlState(isApp, userConfig, false, 'Mail.ReadWrite', 'Allows email to be read for the purposes of server-side filing of incoming tagged messages, and deleted after filing. Access is only required for a single filing mailbox.'),
			]);
		}

		this.scopes = this.scopes.sort((a,b) => { return a.scope > b.scope ? 1 : -1 });

		makeAutoObservable(this);
	}

	grantSelected() {
		var scopes = this.selection.map(s => s.fullScope());

		if(this.isApp) {
			let script = this.generatePowershellScriptForAppPermissions(scopes);
			navigator.clipboard.writeText(script);
			alert("PowerShell script copied to clipboard.");
		} else {
			ScopeCtrlState.openPermissionUrl(scopes, this.userConfig, this.isApp, false);
		}
	}
	generatePowershellScriptForAppPermissions(scopes: string[]) {
		let result = `# PowerShell script to grant permissions for scopes ${scopes.join(", ")}.\r\n\r\n`;

		result += `Install-Module Microsoft.Graph -Scope CurrentUser\r\n\r\n`;

		result += `# First, connect to Microsoft Graph with permissions to edit an application and role assignments.\r\n`
		result += `Connect-MgGraph -Scopes "Application.ReadWrite.All","AppRoleAssignment.ReadWrite.All","Directory.ReadWrite.All"\r\n\r\n`;

		result += `# Get the Helm Background tasks service principal:\r\n`;
		result += `$sp = Get-MgServicePrincipal -Filter "appId eq '${MSALConfigGen.getMsalConfig().auth.appClientId}'"\r\n\r\n`;

		//Get the graph scopes
		let graphScopes = scopes.filter(s => s.toLowerCase().startsWith("https://graph.microsoft.com/"));
		if(graphScopes.length > 0) {
			
			result += `# Get the Microsoft Graph service principal:\r\n`;
			result += `$graphSp = Get-MgServicePrincipal -Filter "appId eq '00000003-0000-0000-c000-000000000000'"\r\n\r\n`;

			graphScopes.forEach(scope => {
				result += `# Get the ${scope} Application Permission (App Role):\r\n`;
				result += `$role = $graphSp.AppRoles |Where-Object { $_.Value -eq "${scope.replace("https://graph.microsoft.com/", "")}" -and $_.AllowedMemberTypes -contains "Application" }\r\n`;

				result += `# Assign the Permission to the Enterprise App:\r\n`;
				result += `New-MgServicePrincipalAppRoleAssignment -ServicePrincipalId $sp.Id -AppRoleId $role.Id -PrincipalId $sp.Id -ResourceId $graphSp.Id\r\n\r\n`;
			});
		}

		let tenantUrl = this.userConfig.tenantUrl.endsWith("/") ? this.userConfig.tenantUrl : `${this.userConfig.tenantUrl}/`;
		let sharePointScopes = scopes.filter(s => s.toLowerCase().startsWith(`${tenantUrl}`));
		if(sharePointScopes.length > 0) {
			result += `# Get the SharePoint service principal:\r\n`;
			result += `$sp = Get-MgServicePrincipal -Filter "appId eq '${MSALConfigGen.getMsalConfig().auth.appClientId}'"\r\n\r\n`;

			sharePointScopes.forEach(scope => {
				result += `# Get the ${scope} Application Permission (App Role):\r\n`;
				result += `$role = $sp.AppRoles |Where-Object { $_.Value -eq "${scope.replace(tenantUrl, "")}" -and $_.AllowedMemberTypes -contains "Application" }\r\n`;

				result += `# Assign the Permission to the Enterprise App:\r\n`;
				result += `New-MgServicePrincipalAppRoleAssignment -ServicePrincipalId $sp.Id -AppRoleId $role.Id -PrincipalId $sp.Id -ResourceId $sp.Id\r\n\r\n`;
			});
		}

		return result;

	}

	grantAll() {
		var scopes = this.scopes.map(s => s.fullScope());
		ScopeCtrlState.openPermissionUrl(scopes, this.userConfig, this.isApp, true);
	}

	refreshAll() {
		this.scopes.forEach(s => {
			s.check();
		});
	}

	getSelection(): Selection {
		let selection = new Selection({
			onSelectionChanged: () => {
				const selectedItems = selection.getSelection() as ScopeCtrlState[];
				this.setSelection(selectedItems);
			},
		});

		return selection;
	}

	public selection: ScopeCtrlState[] = [];
	setSelection(selectedItems: ScopeCtrlState[]) {
		this.selection = selectedItems;
	}


}

function PermissionsList({ state }: { state: PermissionsState }) {
	let columns: IColumn[] = [
		{
			key: 'permissionName',
			name: 'Permission',
			fieldName: 'scope',
			minWidth: 250,
			maxWidth: 250,
		},
		{
			key: 'description',
			name: 'Description',
			fieldName: 'description',
			minWidth: 700,
			maxWidth: 700,
		},
		{
			key: 'status',
			name: 'Status',
			minWidth: 200,
			maxWidth: 200,
			onRender: (item: ScopeCtrlState) => <Scope state={item} />,
		},
	];

	return (
		<Stack>
			<DetailsList items={state.scopes} columns={columns} isHeaderVisible={true} selectionMode={SelectionMode.multiple} selection={state.getSelection()} />
			<Stack horizontal>

				{ state.isApp ? <DefaultButton onClick={state.grantAll.bind(state)}>Grant all permissions</DefaultButton> : <></> }
					
				<DefaultButton disabled={state.selection.length === 0} 
								onClick={state.grantSelected.bind(state)}>
									{state.isApp ? `Grant selected permissions via PowerShell` : `Grant selected permissions`}
				</DefaultButton>
			
				<DefaultButton style={{marginLeft:10}} onClick={state.refreshAll.bind(state)}>Refresh all</DefaultButton>
			</Stack>
		</Stack>
	);
}

export default observer(PermissionsList);
